import { cn } from "@/lib/cn";
import { windowDot } from "@/lib/window";

// ============================================================================
// WindowBadge — the calm form of a window/priority code, for surfaces you
// READ (detail pages, tables) rather than SCAN (the Board).
//
// Same source as the Board (lib/window.ts), lower intensity: a soft slate
// pill carrying the window's hue as a small dot, so "12-4" reads teal-ish
// everywhere without shouting on a focused detail view. The Board keeps its
// loud fills via windowColorClasses for at-a-glance scanning. One meaning,
// two intensities. See DS-04 / DESIGN-REVIEW-2026-05-28.
// ============================================================================

interface Props {
  code: string | null | undefined;
  /** Optional context label rendered before the code, e.g. "Window" / "Priority". */
  prefix?: string;
  className?: string;
}

export function WindowBadge({ code, prefix, className }: Props) {
  if (!code) return null;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full bg-slate-50 px-2.5 py-0.5 text-xs font-medium text-slate-700 ring-1 ring-inset ring-slate-200",
        className,
      )}
    >
      <span
        className={cn("h-1.5 w-1.5 flex-shrink-0 rounded-full", windowDot(code))}
        aria-hidden="true"
      />
      {prefix ? <span className="text-slate-500">{prefix}</span> : null}
      <span className="font-semibold uppercase tracking-wide">{code}</span>
    </span>
  );
}
